/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { isPrd } from "constants/domain"
import { storageHelper } from "utils/storage"
import { TroyWebTracker } from "utils/tracking"
import scribe, { ScribeSDKResponse } from "@grab-x/scribe-web-sdk"
import { get, isUndefined } from "lodash"
import { EventAction, EventCategory } from "constants/tracking"
import { GRABX_FLAG_LIST, EMPTY_VARIABLE_NAME } from "./constants"
import { GrabXDataTypes, convertedScribeSDKValue, TFacets } from "./types"
import { generateDefaultFacets } from "./facets"

let initialized = false

export const initializeScribeSDK = (): void => {
  if (initialized) return

  try {
    // @ts-ignore
    scribe.initialize("troy-portal", isPrd())
    scribe.setAuthToken(storageHelper.getToken() ?? "")
    initialized = true

    TroyWebTracker.sendEvent({
      category: EventCategory.grabx,
      action: EventAction.initialize,
      labels: {
        SUCCESS: true,
      },
    })
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log("error while initialize scribeSDK")
    TroyWebTracker.sendEvent({
      category: EventCategory.grabx,
      action: EventAction.initialize,
      labels: {
        SUCCESS: false,
        ERROR: error,
      },
    })
  }
}

export const convertData = (
  grabXData: convertedScribeSDKValue,
  dataType: GrabXDataTypes,
  defaultValue: any,
): any => {
  const featureValue = get(grabXData, ["feature_value"])
  const featureValueJson = get(grabXData, ["feature_value_json"])

  // use default
  // it can handle grabx backend return empty value or API error as long you define the default value in the constants.ts
  // or the variable is already in localstorage
  let result: any =
    storageHelper.getMerchantPortalFeatureFlag(grabXData.variable_name) ??
    defaultValue

  switch (dataType) {
    case GrabXDataTypes.String:
    case GrabXDataTypes.Number:
    case GrabXDataTypes.Boolean:
      if (!isUndefined(featureValue)) {
        result = featureValue
      }

      break

    // special handle for JSON data types
    case GrabXDataTypes.Json:
      if (!isUndefined(featureValueJson)) {
        result = JSON.parse(featureValueJson)
      } else if (isUndefined(defaultValue)) {
        result = {} as any
      }

      break

    default:
  }

  return result
}

export const getFeature = async (
  variableName: string,
  dataType: GrabXDataTypes,
  facets?: TFacets,
): Promise<convertedScribeSDKValue> => {
  if (variableName === "") {
    return {
      variable_name: variableName,
      error: EMPTY_VARIABLE_NAME,
    }
  }

  let facetArguments = facets

  if (isUndefined(facetArguments)) {
    facetArguments = generateDefaultFacets()
  }

  const result = scribe.getVariable(variableName, facetArguments).then(
    (response: ScribeSDKResponse) => {
      const data: convertedScribeSDKValue = {
        variable_name: variableName,
        feature_value:
          dataType !== GrabXDataTypes.Json ? response.value : undefined,
        feature_value_json:
          dataType === GrabXDataTypes.Json ? response.value : undefined,
      }

      TroyWebTracker.sendEvent({
        category: EventCategory.grabx,
        action: EventAction.getFeature,
        labels: {
          VARIABLE_NAME: variableName,
          SUCCESS: true,
          FACET: facetArguments,
          IS_UNDEFINED: isUndefined(response.value),
        },
      })

      return data
    },
    (error: any) => {
      // eslint-disable-next-line no-console
      console.error(`[GrabX] Failed to fetch feature flag "${variableName}":`, {
        error,
      })

      TroyWebTracker.sendEvent({
        category: EventCategory.grabx,
        action: EventAction.getFeature,
        labels: {
          VARIABLE_NAME: variableName,
          SUCCESS: false,
          ERROR: error,
          FACET: facetArguments,
        },
      })

      return {
        error,
        variable_name: variableName,
        feature_value: undefined,
        feature_value_json: undefined,
      }
    },
  )

  return result
}

export const buildFeatureFlags = (
  results: PromiseSettledResult<convertedScribeSDKValue>[],
): Record<string, any> => {
  const featureFlags: Record<string, any> = {}

  GRABX_FLAG_LIST.forEach((flag, index) => {
    if (flag.grabxVariableName === "") {
      return
    }

    const data: convertedScribeSDKValue = {
      variable_name: get(results[index], ["value", "variable_name"]),
      feature_value: get(results[index], ["value", "feature_value"]),
      feature_value_json: get(results[index], ["value", "feature_value_json"]),
      error: get(results[index], ["value", "error"]),
    }

    featureFlags[flag.grabxVariableName] = convertData(
      data,
      flag.dataType,
      flag.defaultValue,
    )
  })

  return featureFlags
}
